// Local draft storage for the incident form.
//
// Incidents get entered on a phone, at a scene, one-handed — the conditions
// under which a tab gets closed, a browser gets killed for memory, or a battery
// gives out mid-entry. Everything typed before that point used to be gone.
//
// Drafts live in localStorage rather than on the server: the whole point is to
// survive losing the network or the process, and a half-filled incident is not
// something anyone else should see. They are keyed per admin AND per record, so
// two admins sharing a machine can't inherit each other's work and a draft of
// one incident can't surface while editing another.
const TTL_MS = 24 * 60 * 60 * 1000;

export type IncidentDraft = {
  at: number;
  fields: any;
  locality: any;
  locDefinition: any;
};

export const draftKey = (userId: string, incidentId: string | null) =>
  `fw:draft:incident:${incidentId || "new"}:${userId || "anon"}`;

/** Whether anything worth keeping has been entered yet. Without this every
 *  visit to the blank form would leave a draft behind and offer to restore it. */
export function draftHasContent(fields: any): boolean {
  if (!fields) return false;
  const meaningful = [
    fields.locality,
    fields.subLocality,
    fields.address,
    fields.latitude,
    fields.field1Value,
    fields.field2Value,
    fields.field3Value,
    fields.field4Value,
    fields.field5Value,
  ];
  if (meaningful.some((v) => (typeof v === "object" ? Boolean(v?.value) : Boolean(v)))) {
    return true;
  }
  return Array.isArray(fields.respondingUnits) && fields.respondingUnits.length > 0;
}

export function saveDraft(
  userId: string,
  incidentId: string | null,
  payload: Omit<IncidentDraft, "at">
) {
  try {
    localStorage.setItem(
      draftKey(userId, incidentId),
      JSON.stringify({ at: Date.now(), ...payload })
    );
  } catch {
    // A full or disabled localStorage must never take the form down with it.
  }
}

/** Returns a draft only while it is still plausibly the admin's current work.
 *  Anything staler deletes itself rather than offering week-old text. */
export function loadDraft(
  userId: string,
  incidentId: string | null
): IncidentDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(userId, incidentId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IncidentDraft;
    if (!parsed?.at || Date.now() - parsed.at > TTL_MS) {
      dropDraft(userId, incidentId);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function dropDraft(userId: string, incidentId: string | null) {
  try {
    localStorage.removeItem(draftKey(userId, incidentId));
  } catch {
    /* see saveDraft */
  }
}
